<template>
    <div class="block-editor zoho-create-record">
        <div v-if="hasNonZohoChannels" class="integration-warning">
            <div class="warning-content">
                <span class="material-icons warning-icon">warning</span>
                <div class="warning-text">
                    <strong>Entegrasyon Uyarısı</strong>
                    <p>Listede Zoho olmayan kanallar bulunmaktadır. Lütfen bu kanalları entegre edin.</p>
                </div>
            </div>
        </div>
        <div class="wrapper">
            <section>
                <div class="title">{{ $t("message.zohoModuleName") }}e</div>
                <CustomPicker
                    v-model="selectedModule"
                    :items="modules"
                    idKey="api_name"
                    labelKey="plural_label"
                    :title="$t('message.selectModule')"
                />
            </section>

            <section>
                <div class="title">{{ $t("message.standardFields") }}</div>
                <div class="field-picker-wrapper">
                    <CustomPicker
                        v-model="selectedStandardField"
                        :items="availableStandardFields"
                        idKey="api_name"
                        labelKey="field_label"
                        title="Select Standard Field"
                    />
                    <SupsisButton @click="addField(selectedStandardField)">
                        <i class="material-icons">add</i>
                        <span>Add Field</span>
                    </SupsisButton>
                </div>
            </section>

            <section>
                <div class="title">{{ $t("message.customFields") }}</div>
                <div class="field-picker-wrapper">
                    <CustomPicker
                        v-model="selectedCustomField"
                        :items="availableCustomFields"
                        idKey="api_name"
                        labelKey="field_label"
                        :title="$t('message.selectCustomFields')"
                    />
                    <SupsisButton @click="addField(selectedCustomField)">
                        <i class="material-icons">add</i>
                        <span>Add Field</span>
                    </SupsisButton>
                </div>
            </section>

            <section v-for="(field, fieldApiName) in clone.fields" :key="fieldApiName" class="field-section">
                <i
                    v-if="!field.fieldMeta.system_mandatory"
                    class="material-icons remove-field-icon"
                    @click="removeField(fieldApiName)"
                    title="Alanı Kaldır"
                >
                    close
                </i>
                <div class="title">{{ field.fieldMeta.field_label }}</div>
                <CustomPicker
                    v-if="fieldApiName === 'Owner'"
                    v-model="field.value"
                    :items="zohoUsers"
                    idKey="id"
                    labelKey="name"
                    :title="field.fieldMeta.field_label"
                />
                <div class="text-editor-wrapper" v-else-if="field.fieldMeta.data_type !== 'picklist'">
                    <TiptapEditor
                        :placeholder="field.fieldMeta.field_label"
                        :show-attributes="true"
                        :show-emoji="false"
                        v-model="field.value"
                    />
                </div>
                <CustomPicker
                    v-else
                    v-model="field.value"
                    :items="field.fieldMeta.pick_list_values"
                    idKey="actual_value"
                    labelKey="display_value"
                    :title="field.fieldMeta.field_label"
                />
            </section>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import _ from "lodash";
import TiptapEditor from "../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import CustomPicker from "../../../../../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../../../../../Settings/components/SupsisButton.vue";
import { ZOHO_API } from "../../../../../../../api/zoho";
import { mapState } from "vuex";

const log = log2("ZohoCreateRecord");
export default {
    name: "ZohoCreateRecord",
    components: { TiptapEditor, CustomPicker, SupsisButton },
    props: ["value"],
    data() {
        return {
            modules: [],
            selectedModule: null,
            clone: _.cloneDeep(this.value) || {
                module: "",
                fields: {},
            },
            selectedStandardField: null,
            selectedCustomField: null,
            allFields: [],
            zohoUsers: [],
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
        }),
        standardFields() {
            return Object.values(this.clone.fields || {}).filter((field) => !field.fieldMeta.custom_field);
        },
        customFields() {
            return Object.values(this.clone.fields || {}).filter((field) => field.fieldMeta.custom_field);
        },
        availableStandardFields() {
            const addedFields = Object.keys(this.clone.fields || {});
            return this.allFields.filter((field) => !field.custom_field && !addedFields.includes(field.api_name));
        },
        availableCustomFields() {
            const addedFields = Object.keys(this.clone.fields || {});
            return this.allFields.filter((field) => field.custom_field && !addedFields.includes(field.api_name));
        },
        hasNonZohoChannels() {
            if (!this.channels || !Array.isArray(this.channels)) {
                return false;
            }
            return this.channels.some((channel) => channel.type !== "zoho");
        },
    },

    methods: {
        async fetchModules() {
            log("fetchModules");
            const modules = await ZOHO_API.listModules();
            this.modules = modules;
        },
        async fetchUsers() {
            log("fetchUsers");
            const users = await ZOHO_API.listAllUsers();
            this.zohoUsers = users;
        },
        async onModuleChange(module) {
            log("onModuleChange", module);
            this.selectedModule = module;
            const isModuleChanged = module !== this.clone.module;

            if (isModuleChanged) {
                this.clone.module = module;
                this.clone.fields = {};
                this.selectedStandardField = null;
                this.selectedCustomField = null;
            }

            const fields = await ZOHO_API.listModuleFields(module);
            this.allFields = fields;

            if (isModuleChanged) {
                fields.forEach((field) => {
                    if (field.system_mandatory) {
                        this.$set(this.clone.fields, field.api_name, {
                            fieldApiName: field.api_name,
                            value: null,
                            fieldMeta: field,
                        });
                    }
                });
            }
        },
        addField(fieldApiName) {
            log("addField", fieldApiName);
            if (fieldApiName) {
                const fieldMeta = this.allFields.find((f) => f.api_name === fieldApiName);
                if (this.clone.fields[fieldApiName]) {
                    return;
                }
                this.$set(this.clone.fields, fieldApiName, {
                    fieldApiName: fieldApiName,
                    value: null,
                    fieldMeta: fieldMeta,
                });
                this.selectedStandardField = null;
                this.selectedCustomField = null;
                this.clonse = _.cloneDeep(this.clone);
            }
        },
        removeField(fieldApiName) {
            log("removeField", fieldApiName);
            this.$delete(this.clone.fields, fieldApiName);
        },
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                log("clone changed", newValue);
                this.$emit("input", newValue);
            },
        },
        selectedModule: {
            handler(newValue) {
                log("selectedModule changed", newValue);
                this.onModuleChange(newValue);
            },
        },
    },
    created() {
        this.fetchModules();
        this.fetchUsers();
        if (this.value.module) {
            this.onModuleChange(this.value.module);
        }
    },
};
</script>

<style lang="scss">
.block-editor.zoho-create-record {
    .integration-warning {
        margin-bottom: 20px;
        padding: 16px;
        background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
        border-left: 4px solid #f59e0b;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);

        .warning-content {
            display: flex;
            align-items: flex-start;
            gap: 12px;

            .warning-icon {
                font-size: 24px;
                color: #f59e0b;
                flex-shrink: 0;
                margin-top: 2px;
            }

            .warning-text {
                flex: 1;

                strong {
                    display: block;
                    font-size: 14px;
                    font-weight: 600;
                    color: #92400e;
                    margin-bottom: 4px;
                }

                p {
                    margin: 0;
                    font-size: 13px;
                    color: #78350f;
                    line-height: 1.5;
                }
            }
        }
    }

    section {
        margin-bottom: 10px;
    }

    .wrapper {
        padding: 30px;

        .title {
            margin-bottom: 10px;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .text-editor-wrapper {
            border-radius: 0px;
            border: 1px solid #fff;
            background: white;

            &:hover {
                border: 1px solid #006cff !important;
            }
        }
    }

    .field-picker-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .field-section {
        position: relative;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;
        margin-bottom: 15px;

        &:hover .remove-field-icon {
            opacity: 1;
        }
    }

    .remove-field-icon {
        position: absolute;
        top: 10px;
        right: 10px;
        font-size: 20px;
        color: #8c8c8c;
        cursor: pointer;
        opacity: 0.5;
        transition: all 0.2s ease;
        z-index: 10;

        &:hover {
            color: #ff4d4f;
            opacity: 1;
            transform: scale(1.1);
        }
    }
}
</style>
